import { toDateStr } from "./formatting";
import { calculateStats } from "./calculations";
import {
  DEFAULT_DISPLAY_CURRENCY,
  DEFAULT_USD_BRL_RATE,
} from "../constants/currency";
import { getMoneyContext } from "./currency";

const MIME_XLSX =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const COMPLETE_HEADERS = [
  "Data",
  "Inscrições",
  "ABI",
  "Total investido",
  "Total ganho",
  "Lucro",
];
const HEADER_ROW_INDEX = 3;

const STYLE = {
  default: 0,
  header: 1,
  headerDate: 2,
  date: 3,
  integer: 4,
  money: 5,
  profitPositive: 6,
  profitNegative: 7,
  profitNeutral: 8,
  totalDate: 9,
  totalInteger: 10,
  totalMoney: 11,
  totalProfitPositive: 12,
  totalProfitNegative: 13,
  totalProfitNeutral: 14,
};

const isActiveRecord = (record) =>
  !record?.deleted_at && !record?.deletedAt && !record?.DeletedAt;

const xmlEscape = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const formatDatePtBr = (dateStr) => {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

const getMonthSheetName = (monthKey) => {
  const [year, month] = monthKey.split("-");
  return `${month}-${year}`;
};

const getCurrencyInfoRows = (options = {}) => {
  const moneyContext = getMoneyContext(options);
  return [
    ["Moeda da exportacao:", moneyContext.displayCurrency, "", "", "", ""],
    [
      "Cotacao sistemica usada:",
      `1 USD = R$ ${moneyContext.usdBrlRate.toFixed(2).replace(".", ",")}`,
      "",
      "",
      "",
      "",
    ],
    ["", "", "", "", "", ""],
  ];
};

const calculateDailyCompleteMetrics = (dayRecords, tournaments, options = {}) => {
  const stats = calculateStats(dayRecords, tournaments, options);
  return {
    inscricoes: stats.totalInsc,
    abi: stats.abi,
    totalInvestido: stats.totalInvestido,
    totalGanho: stats.totalGanho,
    lucro: stats.totalGanho - stats.totalInvestido,
  };
};

const metricsToRow = (label, metrics) => [
  label,
  metrics.inscricoes,
  metrics.abi,
  metrics.totalInvestido,
  metrics.totalGanho,
  metrics.lucro,
];

const getRecordsInMonth = (recordsByDate, monthKey) =>
  Object.entries(recordsByDate)
    .filter(([date]) => date.startsWith(monthKey))
    .flatMap(([, records]) => records);

const getRecordsInYear = (recordsByDate, year) =>
  Object.entries(recordsByDate)
    .filter(([date]) => date.startsWith(`${year}-`))
    .flatMap(([, records]) => records);

const createAnnualSummarySheet = (recordsByDate, tournaments, year, options = {}) => {
  const rows = [...getCurrencyInfoRows(options), COMPLETE_HEADERS];

  for (let month = 1; month <= 12; month += 1) {
    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    const monthRecords = getRecordsInMonth(recordsByDate, monthKey);

    rows.push(
      metricsToRow(
        `${String(month).padStart(2, "0")}/${year}`,
        calculateDailyCompleteMetrics(monthRecords, tournaments, options)
      )
    );
  }

  rows.push(
    metricsToRow(
      "Total",
      calculateDailyCompleteMetrics(
        getRecordsInYear(recordsByDate, year),
        tournaments,
        options
      )
    )
  );

  return {
    name: String(year),
    rows,
  };
};

export const buildCompleteSpreadsheetSheets = (
  registros,
  tournaments,
  options = {}
) => {
  const recordsByDate = registros.filter(isActiveRecord).reduce((acc, registro) => {
    const dateStr = toDateStr(registro.registradoEm);
    if (!dateStr) return acc;
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(registro);
    return acc;
  }, {});

  const monthKeys = [...new Set(Object.keys(recordsByDate).map((date) => date.slice(0, 7)))]
    .sort();

  if (monthKeys.length === 0) return [];

  const monthlySheets = monthKeys.map((monthKey) => {
    const [yearStr, monthStr] = monthKey.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    const daysInMonth = getDaysInMonth(year, month);
    const rows = [...getCurrencyInfoRows(options), COMPLETE_HEADERS];

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dayStr = String(day).padStart(2, "0");
      const dateStr = `${monthKey}-${dayStr}`;
      const metrics = calculateDailyCompleteMetrics(
        recordsByDate[dateStr] || [],
        tournaments,
        options
      );

      rows.push([
        formatDatePtBr(dateStr),
        metrics.inscricoes,
        metrics.abi,
        metrics.totalInvestido,
        metrics.totalGanho,
        metrics.lucro,
      ]);
    }

    rows.push(
      metricsToRow(
        "Total",
        calculateDailyCompleteMetrics(
          getRecordsInMonth(recordsByDate, monthKey),
          tournaments,
          options
        )
      )
    );

    return {
      name: getMonthSheetName(monthKey),
      rows,
    };
  });

  const yearSheets = [...new Set(monthKeys.map((monthKey) => monthKey.slice(0, 4)))]
    .map((year) => createAnnualSummarySheet(recordsByDate, tournaments, year, options));

  return [...monthlySheets, ...yearSheets];
};

const colName = (index) => {
  let n = index + 1;
  let name = "";
  while (n > 0) {
    const remainder = (n - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
};

const getCellStyle = (value, rowIndex, colIndex, rowCount) => {
  if (rowIndex === HEADER_ROW_INDEX) {
    return colIndex === 0 ? STYLE.headerDate : STYLE.header;
  }

  const isTotalRow = rowIndex === rowCount - 1;
  if (isTotalRow) {
    if (colIndex === 0) return STYLE.totalDate;
    if (colIndex === 1 || colIndex === 2) return STYLE.totalInteger;
    if (colIndex === 5) {
      if (Number(value) > 0) return STYLE.totalProfitPositive;
      if (Number(value) < 0) return STYLE.totalProfitNegative;
      return STYLE.totalProfitNeutral;
    }
    return STYLE.totalMoney;
  }

  if (rowIndex < HEADER_ROW_INDEX) return STYLE.default;
  if (colIndex === 0) return STYLE.date;
  if (colIndex === 1 || colIndex === 2) return STYLE.integer;
  if (colIndex === 5) {
    if (Number(value) > 0) return STYLE.profitPositive;
    if (Number(value) < 0) return STYLE.profitNegative;
    return STYLE.profitNeutral;
  }
  return STYLE.money;
};

const createCellXml = (value, rowIndex, colIndex, rowCount) => {
  const ref = `${colName(colIndex)}${rowIndex + 1}`;
  const style = getCellStyle(value, rowIndex, colIndex, rowCount);
  if (typeof value === "number") {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `<c r="${ref}" s="${style}"><v>${safeValue}</v></c>`;
  }
  return `<c r="${ref}" s="${style}" t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;
};

const createWorksheetXml = (rows) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews>
    <sheetView workbookViewId="0">
      <pane ySplit="4" topLeftCell="A5" activePane="bottomLeft" state="frozen"/>
    </sheetView>
  </sheetViews>
  <cols>
    <col min="1" max="1" width="14" customWidth="1"/>
    <col min="2" max="2" width="13" customWidth="1"/>
    <col min="3" max="3" width="11" customWidth="1"/>
    <col min="4" max="6" width="17" customWidth="1"/>
  </cols>
  <sheetData>
    ${rows
      .map(
        (row, rowIndex) =>
          `<row r="${rowIndex + 1}">${row
            .map((cell, colIndex) =>
              createCellXml(cell, rowIndex, colIndex, rows.length)
            )
            .join("")}</row>`
      )
      .join("")}
  </sheetData>
  <autoFilter ref="A4:F${rows.length}"/>
</worksheet>`;

const createWorkbookXml = (sheets) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    ${sheets
      .map(
        (sheet, index) =>
          `<sheet name="${xmlEscape(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
      )
      .join("")}
  </sheets>
</workbook>`;

const createWorkbookRelsXml = (sheets) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${sheets
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
    )
    .join("")}
  <Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

const createContentTypesXml = (sheets) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join("")}
</Types>`;

const ROOT_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="2">
    <numFmt numFmtId="164" formatCode="#,##0.00"/>
    <numFmt numFmtId="165" formatCode="0"/>
  </numFmts>
  <fonts count="5">
    <font><sz val="11"/><name val="Oswald"/></font>
    <font><b/><sz val="11"/><name val="Oswald"/><color rgb="FF000000"/></font>
    <font><sz val="11"/><name val="Oswald"/><color rgb="FF008000"/></font>
    <font><sz val="11"/><name val="Oswald"/><color rgb="FFC00000"/></font>
    <font><b/><sz val="11"/><name val="Oswald"/><color rgb="FF000000"/></font>
  </fonts>
  <fills count="5">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE7A42C"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFF2D6"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE7E6E6"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="5">
    <border>
      <left style="thin"><color rgb="FFD9D9D9"/></left>
      <right style="thin"><color rgb="FFD9D9D9"/></right>
      <top style="thin"><color rgb="FFD9D9D9"/></top>
      <bottom style="thin"><color rgb="FFD9D9D9"/></bottom>
    </border>
    <border>
      <left style="thin"><color rgb="FF000000"/></left>
      <right style="thin"><color rgb="FF000000"/></right>
      <top style="thin"><color rgb="FF000000"/></top>
      <bottom style="medium"><color rgb="FF000000"/></bottom>
    </border>
    <border>
      <left style="thin"><color rgb="FF000000"/></left>
      <right style="medium"><color rgb="FF000000"/></right>
      <top style="thin"><color rgb="FF000000"/></top>
      <bottom style="medium"><color rgb="FF000000"/></bottom>
    </border>
    <border>
      <left style="thin"><color rgb="FFD9D9D9"/></left>
      <right style="medium"><color rgb="FF000000"/></right>
      <top style="thin"><color rgb="FFD9D9D9"/></top>
      <bottom style="thin"><color rgb="FFD9D9D9"/></bottom>
    </border>
    <border>
      <left style="thin"><color rgb="FF000000"/></left>
      <right style="thin"><color rgb="FF000000"/></right>
      <top style="medium"><color rgb="FF000000"/></top>
      <bottom style="medium"><color rgb="FF000000"/></bottom>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="15">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyBorder="1"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="3" xfId="0" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="2" fillId="4" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="3" fillId="4" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="0" fillId="4" borderId="0" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="4" fillId="3" borderId="4" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="165" fontId="4" fillId="3" borderId="4" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="4" fillId="3" borderId="4" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="2" fillId="3" borderId="4" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="3" fillId="3" borderId="4" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="4" fillId="3" borderId="4" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="right" vertical="center"/></xf>
  </cellXfs>
</styleSheet>`;

const crcTable = (() => {
  const table = [];
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const uint16 = (value) => {
  const bytes = new Uint8Array(2);
  new DataView(bytes.buffer).setUint16(0, value, true);
  return bytes;
};

const uint32 = (value) => {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value, true);
  return bytes;
};

const concatBytes = (parts) => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};

const encodeText = (text) => {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(text);
  }

  const bytes = [];
  for (const char of String(text)) {
    const codePoint = char.codePointAt(0);
    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }
  return new Uint8Array(bytes);
};

const createZip = (files) => {
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encodeText(file.name);
    const contentBytes = encodeText(file.content);
    const crc = crc32(contentBytes);
    const size = contentBytes.length;
    const localHeader = concatBytes([
      uint32(0x04034b50),
      uint16(20),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(crc),
      uint32(size),
      uint32(size),
      uint16(nameBytes.length),
      uint16(0),
      nameBytes,
    ]);
    const centralHeader = concatBytes([
      uint32(0x02014b50),
      uint16(20),
      uint16(20),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(crc),
      uint32(size),
      uint32(size),
      uint16(nameBytes.length),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(0),
      uint32(offset),
      nameBytes,
    ]);

    localParts.push(localHeader, contentBytes);
    centralParts.push(centralHeader);
    offset += localHeader.length + contentBytes.length;
  });

  const centralDirectory = concatBytes(centralParts);
  const end = concatBytes([
    uint32(0x06054b50),
    uint16(0),
    uint16(0),
    uint16(files.length),
    uint16(files.length),
    uint32(centralDirectory.length),
    uint32(offset),
    uint16(0),
  ]);

  return new Blob([concatBytes([...localParts, centralDirectory, end])], {
    type: MIME_XLSX,
  });
};

export const createCompleteSpreadsheetBlob = (registros, tournaments, options = {}) => {
  const sheets = buildCompleteSpreadsheetSheets(registros, tournaments, {
    displayCurrency: options.displayCurrency || DEFAULT_DISPLAY_CURRENCY,
    usdBrlRate: options.usdBrlRate || DEFAULT_USD_BRL_RATE,
  });

  if (sheets.length === 0) {
    throw new Error("Nao ha registros para exportar.");
  }

  const files = [
    { name: "[Content_Types].xml", content: createContentTypesXml(sheets) },
    { name: "_rels/.rels", content: ROOT_RELS_XML },
    { name: "xl/workbook.xml", content: createWorkbookXml(sheets) },
    { name: "xl/_rels/workbook.xml.rels", content: createWorkbookRelsXml(sheets) },
    { name: "xl/styles.xml", content: STYLES_XML },
    ...sheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      content: createWorksheetXml(sheet.rows),
    })),
  ];

  return createZip(files);
};

export const exportCompleteSpreadsheet = (registros, tournaments, options = {}) => {
  const blob = createCompleteSpreadsheetBlob(registros, tournaments, options);
  const filename = `orion33-planilha-completo-${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
  return { success: true };
};
